import { SXButton } from "@common/components/ui-components/SXButton";
import React, { JSX, useEffect, useState } from "react";
import { Alert } from "react-bootstrap";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { v4 as uuidv4 } from "uuid";

interface SXAlertProps {
  status: number | string | JSX.Element;
  text: string | JSX.Element;
  context?: string;
  variant?: string;
  keep?: boolean;
  // Called when the alert has faded out or been closed. Whoever mounted this component owns the
  // container and the React root, so tearing down is their job - see AlertMessage. Optional
  // because SXAlert is also rendered inline inside an ordinary tree, where there is nothing to
  // tear down and the alert simply fades to nothing in place.
  onDismiss?: () => void;
  // Lay the alert out in normal flow instead of floating it over its parent, so a container can
  // stack several of them. Set by AlertMessage; the inline usage leaves it off.
  inFlow?: boolean;
}

export const SXAlert = (props: SXAlertProps) => {
  const [opacity, setOpacity] = useState<number>(20);
  const alertId = `alert-${uuidv4()}`;

  useEffect(() => {
    setOpacity(20.0);
  }, []);

  useEffect(() => {
    if (props.keep) return;

    if (opacity > 0) {
      setTimeout(() => {
        setOpacity(opacity - 0.3);
      }, 108);
    }
    if (opacity <= 0) {
      dismiss();
    }
  }, [opacity]);

  const onClick = () => {
    dismiss();
  };

  // This used to call createRoot() on its own parent and unmount that, which meant creating a
  // second root on a container AlertMessage had already rooted (React logs an error and the
  // container element itself was left behind), and when SXAlert is rendered inline it would have
  // unmounted a slice of someone else's tree. The owner is told instead.
  const dismiss = () => {
    props.onDismiss?.();
  };

  // Floating on its own by default, for the inline usage that has to sit over its panel. Alerts
  // raised through AlertMessage go into a shared stack instead and must take part in its column
  // layout, so they ask for normal flow.
  const style: React.CSSProperties = props.inFlow
    ? { opacity: opacity, whiteSpace: "pre-line", marginBottom: 0 }
    : { opacity: opacity, position: "absolute", bottom: "5%", right: "1%", zIndex: 9999, whiteSpace: "pre-line" };

  return (
    <>
      {props.context ? (
        <Alert style={style} variant={props.variant} id={alertId} className="sxalert no-print">
          <SXButton onClick={onClick} icon={faTimes} />
          <strong>{props.status}:</strong> ({props.context}) {props.text}
        </Alert>
      ) : (
        <Alert style={style} variant={props.variant} id={alertId} className="sxalert no-print">
          <SXButton onClick={onClick} icon={faTimes} />
          <strong>{props.status}:</strong> {props.text}
        </Alert>
      )}
    </>
  );
};
